/**
 * usePipMoments + PipMomentsProvider — the Pip governor (Wave 2 keystone).
 *
 * Spec: .plan/features/2026-06-10-pip-mascot-placement.md §3 + Rev 3.3.
 *
 * The governor is the SINGLE owner of every moment trigger. Placements never
 * mount their own animating <PipMascot>; they call `fire(momentKey)` (or
 * `setWorking(boolean)` for the knead loop) and the provider's singleton render
 * slot plays the one animation. The provider:
 *
 *   - mounts ONCE, near the toast host (so moment-Pip and toasts share a layout
 *     region) and owns the single moment-Pip render slot;
 *   - enforces "never two animations at once" GLOBALLY — per-call hooks cannot
 *     coordinate across independently mounted <PipMascot> instances, so a single
 *     provider-owned slot is what makes the collision rule enforceable;
 *   - precedence on collision: `settle` (failure) preempts everything. A
 *     celebration playing beside a failure toast is incoherent. A preempted
 *     `congratulate` is STILL marked consumed (the ledger write happens in
 *     recordAndShouldFire at decision time, before the slot is updated), so a
 *     missed celebration is quieter than a wrong one and firsts never replay
 *     because of a race.
 *
 * The empty-state (P1) and 404 (P7) Pips are standalone idle <PipMascot> renders
 * that do NOT go through this provider — they never animate on a moment.
 *
 * Reduced motion (plan §3): the first is still recorded; the component's
 * prefers-reduced-motion CSS kills the paint. No JS branch here.
 *
 * Budget: logic + one mount. No new dependencies. The Pip render reuses the
 * already-shipped <PipMascot>.
 */

import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react";
import { PipMascot, type PipAction } from "./PipMascot";
import { recordAndShouldFire, type PipMomentKey } from "./pipMoments";
import "./pipMoments.css";

// ── Public API ──────────────────────────────────────────────────────────────────

export interface PipMomentsApi {
  /**
   * Fire a moment. Resolves the moment's action + frequency from the §3 table,
   * records the firing (once-ever / once-session ledger), and plays the action
   * in the provider's single slot when the frequency policy allows.
   *
   * Collision: if something is already animating, a `save-failed` (settle)
   * preempts it; any other moment is dropped from the slot (but a once-ever
   * first is still recorded as consumed — see module header).
   *
   * `fire('working')` is accepted but the knead loop is better driven through
   * `setWorking` — a one-shot fire('working') plays a single bounded knead
   * cycle; `setWorking(true/false)` loops it while the host's pending state
   * holds. `fire` returns void.
   */
  fire: (key: PipMomentKey) => void;

  /**
   * Start/stop the `working` knead loop. `setWorking(true)` mounts a looping
   * knead bounded by host state; `setWorking(false)` clears it. This is the
   * clean API for plan §3's "knead while pending >1.5s (loopWhile)" — the host
   * owns the >1.5s debounce + the start/stop calls. A `save-failed` settle
   * preempts an active working loop (settle precedence); when the settle ends,
   * the slot clears (the host is expected to clear working on error anyway).
   *
   * No live consumer ships in this wave (P4 progress surface is descoped); this
   * is the documented seam plus unit coverage.
   */
  setWorking: (on: boolean) => void;
}

// ── Context ─────────────────────────────────────────────────────────────────────

const PipMomentsContext = createContext<PipMomentsApi | null>(null);

/**
 * Access the governor. Must be called inside a <PipMomentsProvider>. Throwing on
 * a missing provider is a developer error (a placement mounted outside the
 * provider), surfaced loudly rather than silently no-op'ing.
 */
export function usePipMoments(): PipMomentsApi {
  const ctx = useContext(PipMomentsContext);
  if (ctx === null) {
    throw new Error("usePipMoments must be used within a <PipMomentsProvider>.");
  }
  return ctx;
}

// ── Slot state ──────────────────────────────────────────────────────────────────

interface SlotState {
  /** The action currently playing in the slot, or null when idle/empty. */
  action: PipAction | null;
  /** True only for the working knead loop (drives loopWhile on the mascot). */
  loop: boolean;
  /**
   * A monotonically increasing token. Bumped on every fire so a repeated
   * `every-time` action (two save-failed in a row) re-keys the <PipMascot>,
   * forcing a fresh mount → fresh animation even when the action is identical.
   */
  nonce: number;
}

const EMPTY_SLOT: SlotState = { action: null, loop: false, nonce: 0 };

// ── Provider ────────────────────────────────────────────────────────────────────

export interface PipMomentsProviderProps {
  children: ReactNode;
  /** Pixel size of the moment-Pip slot. Defaults to 56 (the congratulate mockup size). */
  size?: number;
}

/**
 * Singleton provider. Mount ONCE, near the toast host. Owns the single
 * moment-Pip render slot.
 */
export function PipMomentsProvider({ children, size = 56 }: PipMomentsProviderProps) {
  const [slot, setSlot] = useState<SlotState>(EMPTY_SLOT);

  // A ref mirror of the slot so the stable `fire` / `setWorking` callbacks can
  // read the latest slot without re-creating on every animation (keeps the
  // context value referentially stable → no needless placement re-renders).
  const slotRef = useRef<SlotState>(EMPTY_SLOT);
  slotRef.current = slot;

  // ── fire ──────────────────────────────────────────────────────────────────
  const fire = useCallback((key: PipMomentKey) => {
    // recordAndShouldFire writes the ledger at decision time. Doing this FIRST
    // is what makes a preempted/dropped once-ever first stay consumed: even if
    // the collision rule drops this from the slot below, the ledger already
    // recorded it (a missed celebration is quieter than a wrong/replayed one).
    const { fire: shouldFire, action } = recordAndShouldFire(key);
    if (!shouldFire) return;

    const current = slotRef.current;
    const isAnimating = current.action !== null;
    const isFailure = action === "settle";

    // Collision rule (plan §3): never two animations at once.
    //   - settle (failure) preempts everything → always takes the slot.
    //   - anything else, while something is animating, is dropped from the slot
    //     (the ledger write above already happened, so firsts don't replay).
    if (isAnimating && !isFailure) {
      return;
    }

    setSlot((prev) => ({ action, loop: false, nonce: prev.nonce + 1 }));
  }, []);

  // ── setWorking ──────────────────────────────────────────────────────────────
  const setWorking = useCallback((on: boolean) => {
    if (on) {
      const current = slotRef.current;
      // Do not stomp an in-flight one-shot action (e.g. a settle) with the loop.
      // The working loop only takes the slot when nothing else is animating.
      if (current.action !== null && current.action !== "knead") return;
      setSlot((prev) => ({ action: "knead", loop: true, nonce: prev.nonce + 1 }));
    } else {
      // Only clear if WE own the slot (the knead loop). A one-shot action that
      // happens to be playing is left to finish via its own onActionEnd.
      setSlot((prev) => (prev.action === "knead" && prev.loop ? EMPTY_SLOT : prev));
    }
  }, []);

  // ── Slot clear on animation end ──────────────────────────────────────────────
  const handleActionEnd = useCallback(() => {
    // The looping knead never fires animationend (it loops); it is cleared only
    // by setWorking(false). For every one-shot action, clear the slot so the
    // next fire starts from empty (and the collision rule sees an idle slot).
    setSlot((prev) => (prev.loop ? prev : EMPTY_SLOT));
  }, []);

  const api = useMemo<PipMomentsApi>(() => ({ fire, setWorking }), [fire, setWorking]);

  return (
    <PipMomentsContext.Provider value={api}>
      {children}
      {/*
        The single moment-Pip render slot. Lives near the toast host (the
        provider wraps the authenticated shell whose .proof-app-route holds the
        sonner <Toaster>). Fixed, top-centred, above content, pointer-events
        none — it announces nothing (aria-hidden); the adjacent toast carries
        the words. Hidden entirely (display:none via the empty-slot class) when
        no moment is playing, so it never occupies layout at rest.

        keyed on `nonce` so a repeated every-time action re-mounts <PipMascot>
        and replays from the start.
      */}
      <div
        className="proof-pip-moment-slot"
        data-testid="pip-moment-slot"
        data-pip-action={slot.action ?? undefined}
        data-empty={slot.action === null ? "" : undefined}
        aria-hidden="true"
      >
        {slot.action !== null && (
          <PipMascot
            key={slot.nonce}
            size={size}
            action={slot.action}
            loopWhile={slot.loop}
            onActionEnd={handleActionEnd}
            ariaHidden
          />
        )}
      </div>
    </PipMomentsContext.Provider>
  );
}
